import './style.css';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@economic/taco';
import { Button, Group, Switch } from '@economic/taco';

import internalApiClient from '../../../../../../shared/util/api/internalApiClient';
import MoneyflowInvoiceStatusDetails from './MoneyflowInvoiceStatusDetails';
import SpinnerWithText from '../../../../../../shared/components/ui/SpinnerWithText/SpinnerWithText';
import useQuery from '../../../../../../shared/hooks/useQuery';
import { Column, Row } from '../../../../../../shared/components/Grid/Grid';
import { MoneyflowOfferOverviewResult } from '../../../../../../../types/internal-api';
import { t, tBB, useLocalization } from '../../../../../../shared/components/l10n/l10n';

interface MoneyflowCongratsDialogProps {
    offer: MoneyflowOfferOverviewResult;
    onHide?: () => void;
}

async function updateCustomerDefaultSpotFactoring(customerId: number, customer: any): Promise<any> {
    return await internalApiClient.put(`/customers/${customerId}`, customer);
}

export default function MoneyflowPostSaleDialog({ offer, onHide }: MoneyflowCongratsDialogProps) {
    const navigate = useNavigate();
    const { data: customer, loading: customerLoading } = useQuery('/customers/' + offer.customerId);
    const loadingText = useLocalization(textIds);
    const [defaultUseSpotFactoring, setDefaultUseSpotFactoring] = React.useState<boolean | undefined>();

    const showToggler = React.useMemo(() => {
        if (customerLoading) {
            return false;
        }
        return !customer.defaultUseSpotFactoring;
    }, [customerLoading]);

    if (loadingText) {
        return null;
    }

    async function toggleDefaultUseSpotFactoring(defaultUseSpotFactoring: boolean) {
        setDefaultUseSpotFactoring(defaultUseSpotFactoring);
        customer.defaultUseSpotFactoring = defaultUseSpotFactoring;
        await updateCustomerDefaultSpotFactoring(offer.customerId, customer);
    }

    return (
        <Dialog defaultOpen size="sm" onClose={onHide}>
            <Dialog.Content aria-label={t`FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_HEADER`}>
                {customerLoading ? (
                    <SpinnerWithText />
                ) : (
                    <>
                        <Dialog.Title>
                            <div className="mb-12">{t`FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_HEADER`}</div>
                        </Dialog.Title>

                        <Row>
                            <Column>
                                <div>
                                    <MoneyflowInvoiceStatusDetails offer={offer} customer={customer} />
                                    {showToggler && (
                                        <Switch
                                            checked={defaultUseSpotFactoring || customer.defaultUseSpotFactoring}
                                            label={t`FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_TOGGLE_TEXT`}
                                            onChange={() =>
                                                toggleDefaultUseSpotFactoring(
                                                    !(defaultUseSpotFactoring || customer.defaultUseSpotFactoring)
                                                )
                                            }
                                        />
                                    )}
                                </div>
                            </Column>
                            <Column>
                                <div className="ml-6">
                                    <h3 className="font-bold text-base">{t`FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_INFO_HEADER`}</h3>
                                    {tBB('FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_INFO_TEXT')}
                                </div>
                            </Column>
                        </Row>

                        <Dialog.Footer>
                            <Group>
                                <Dialog.Close>
                                    <Button appearance="discrete" onClick={() => navigate('/sales/invoicing/invoices/new')}>
                                        {t`FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_BUTTON_NEW_INVOICE`}
                                    </Button>
                                </Dialog.Close>
                                <Dialog.Close>
                                    <Button appearance="primary" onClick={() => navigate('/sales/invoicing/instant-payment')}>
                                        {t`FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_BUTTON_DASHBOARD`}
                                    </Button>
                                </Dialog.Close>
                            </Group>
                        </Dialog.Footer>
                    </>
                )}
            </Dialog.Content>
        </Dialog>
    );
}

const textIds = [
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_HEADER',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_TOGGLE_TEXT',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_INFO_HEADER',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_INFO_TEXT',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_BUTTON_NEW_INVOICE',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_BUTTON_DASHBOARD',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_DETAILS_HEADER',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_DETAILS_CUSTOMER',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_DETAILS_AMOUNT',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_DETAILS_STATUS',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_DETAILS_STATUS_VALUE',
    'FORM_BOOKANDSEND_MONEYFLOW_POSTSALE_DETAILS_DATE',
];
